<script setup lang="ts">
import lodashPick from 'lodash-es/pick';

export interface StoryblokVideoProps {
    blok: StoryblokVideo;
}

type ExternalEmbed = {
    type: 'youtube';
    id: string;
    src: string;
    allowfullscreen: boolean;
};

// PROPS
const { blok } = defineProps<StoryblokVideoProps>();

// COMPOSABLES
const { classes } = useStoryblokComponentClass(() => lodashPick(blok, ['maxWidth', 'rounded']));

// DATA
const asset = computed<StoryblokAsset>(() => blok.asset);
const filename = computed<string | undefined>(() => asset.value.filename ?? undefined);
const attributes = computed(() => {
    switch (blok.mode) {
        case 'ambient':
            return {
                autoplay: true,
                muted: true,
                looping: true,
                controls: false,
            };
        case 'playback':
            return {
                autoplay: false,
                muted: false,
                looping: false,
                controls: true,
            };
    }
});
const embed = computed<ExternalEmbed | undefined>(() => {
    if (!asset.value.is_external_url || !filename.value) {
        return;
    }

    // Youtube
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = filename.value.match(regExp);

    if (match && match[7].length === 11) {
        return {
            type: 'youtube',
            id: match[7],
            src: `https://www.youtube-nocookie.com/embed/${match[7]}?${blok.mode === 'ambient' ? `autoplay=1&mute=1&loop=1&playlist=${match[7]}&controls=0` : 'controls=1'}`,
            allowfullscreen: blok.mode === 'playback',
        };
    }
});
</script>

<template>
    <div v-editable="blok" class="aspect-video w-full" :class="classes">
        <iframe
            v-if="embed && embed.type === 'youtube'"
            :src="embed.src"
            class="h-full w-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            :allowfullscreen="embed.allowfullscreen"
            loading="lazy"
            referrerpolicy="strict-origin-when-cross-origin"
        />
        <video v-else-if="!asset.is_external_url" class="h-full w-full border-0" v-bind="attributes" playsinline :src="filename" />
    </div>
</template>
